import { useEffect, useRef } from 'react';
import type { MouseEvent, SyntheticEvent } from 'react';

/**
 * Modal behaviour for the Scenario Center's native `<dialog>` elements.
 *
 * WHY A NATIVE MODAL DIALOG AND NOT A PORTALLED OVERLAY
 *   `showModal()` puts the element in the browser's TOP LAYER, where its containing block is
 *   the initial containing block. It therefore covers the viewport regardless of a transformed
 *   or overflow-clipped ancestor, and the platform's own CSS keeps applying because the element
 *   stays where it is in the DOM tree. Portalling to `document.body` would buy nothing here and
 *   would break every `.scc-page .scc-drawer` rule at once. The claim is not taken on trust: the
 *   browser test opens the drawer at the top, the middle and the bottom of the page and measures
 *   the dialog and its backdrop against the viewport each time.
 *
 * WHY THE FOCUS RESTORE RUNS IN THE CLEANUP
 *   A modal dialog makes the rest of the document inert, so focusing the element that opened it
 *   WHILE IT IS STILL OPEN is silently ignored. React runs the effect cleanup after it has
 *   removed the dialog from the document — at which point the dialog is no longer modal.
 *
 * WHY THE OPENER IS PASSED IN
 *   A card opened with the keyboard, a card opened with a pointer and a control opened
 *   programmatically do not agree on what `document.activeElement` is at that moment. The
 *   trigger knows its own element; it says so. If that element is gone by the time the drawer
 *   closes — the issue changed, a section was re-rendered — focus falls back to the current
 *   issue, which is where the reader actually is, rather than to `body`.
 */

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableIn(element: HTMLElement): HTMLElement[] {
  return [...element.querySelectorAll<HTMLElement>(focusableSelector)].filter(
    (node) => node.offsetParent !== null || node === document.activeElement,
  );
}

/**
 * Where focus goes when the trigger no longer exists.
 *
 * The order is the reader's own path backwards: the current issue, then the scene it belongs to,
 * then the library's search box, then whatever had focus before. Landing on `body` — which is
 * what an unguarded `element.focus()` does once the trigger is unmounted — leaves a keyboard user
 * at the top of the document with no idea where they were.
 */
function restoreFocus(target: HTMLElement | null | undefined, fallback: HTMLElement | null) {
  const candidates: (HTMLElement | null)[] = [
    target?.isConnected ? target : null,
    document.querySelector<HTMLElement>('.scc-issue-list [aria-current="true"]'),
    document.querySelector<HTMLElement>('.scc-scene-back'),
    document.querySelector<HTMLElement>('#scc-library-search'),
    fallback?.isConnected ? fallback : null,
    document.querySelector<HTMLElement>('.scc-card'),
  ];
  for (const candidate of candidates) {
    if (!candidate) continue;
    candidate.focus();
    if (document.activeElement === candidate) return;
  }
}

function lockScroll(): () => void {
  const body = document.body;
  const previousOverflow = body.style.overflow;
  const previousPadding = body.style.paddingRight;
  const scrollbar = window.innerWidth - document.documentElement.clientWidth;
  body.style.overflow = 'hidden';
  if (scrollbar > 0) body.style.paddingRight = scrollbar + 'px';
  return () => {
    body.style.overflow = previousOverflow;
    body.style.paddingRight = previousPadding;
  };
}

export function useModalFocus(restoreFocusTo: HTMLElement | null | undefined) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    const fallback = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const unlock = lockScroll();
    element.showModal();
    const trap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !dialog.current) return;
      const items = focusableIn(dialog.current);
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      const inside = dialog.current.contains(active);
      if (event.shiftKey && (!inside || active === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (!inside || active === last)) {
        event.preventDefault();
        first.focus();
      }
    };
    element.addEventListener('keydown', trap);
    return () => {
      element.removeEventListener('keydown', trap);
      element.close();
      unlock();
      restoreFocus(restoreFocusTo, fallback);
    };
  }, [restoreFocusTo]);
  return dialog;
}

/**
 * The dismiss behaviour every Scenario Center dialog shares: Escape and a backdrop click both
 * route through the same close callback, so closing always takes one path.
 */
export function modalDismissProps(onClose: () => void) {
  return {
    onCancel: (event: SyntheticEvent<HTMLDialogElement>) => {
      event.preventDefault();
      onClose();
    },
    onClick: (event: MouseEvent<HTMLDialogElement>) => {
      if (event.target === event.currentTarget) onClose();
    },
  } as const;
}
